import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircleIcon, CreditCardIcon, Loader2Icon, LockIcon } from 'lucide-react';
import { formatCardNumber, formatExpiry } from '../../utils/checkout';
import { formatPrice } from '../../utils/currency';
import { easeOut } from '../../utils/motion';
import type { CheckoutState } from '../../hooks/useCheckout';
import { FormField } from './FormField';
export function PaymentStep({
  checkout
}: {
  checkout: CheckoutState;
}) {
  const {
    form,
    errors,
    update,
    placeOrder,
    status,
    total
  } = checkout;
  const processing = status === 'processing';
  return <form noValidate onSubmit={e => {
    e.preventDefault();
    placeOrder();
  }} data-id="element-180" data-mp="15yf3qj2v37tq">
      <AnimatePresence data-id="element-181" data-mp="19yz0hljiw9fc">
        {status === 'declined' && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: easeOut
      }} role="alert" className="mb-5 flex gap-3 rounded-md border border-clay/40 bg-clay/10 p-4 text-sm" data-id="element-182" data-mp="1xq4dzu1zqpmh">
            <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" data-id="element-183" data-mp="3i8snc1xk4hw7" />
            <div data-id="element-184" data-mp="rj481ink8lh">
              <p className="font-medium" data-id="element-185" data-mp="s2h33tg46sn">Paiement refusé par votre banque</p>
              <p className="mt-0.5 text-muted" data-id="element-186" data-mp="3s441ai155l2w">Aucun montant n’a été débité. Vérifiez vos informations ou essayez une autre carte.</p>
            </div>
          </motion.div>}
      </AnimatePresence>

      <fieldset disabled={processing} className="grid gap-4 sm:grid-cols-2" data-id="element-187" data-mp="5osrpbmhea6o">
        <legend className="sr-only" data-id="element-188" data-mp="jplpc582xle0">Carte bancaire</legend>
        <FormField id="cardNumber" label="Numéro de carte" inputMode="numeric" autoComplete="cc-number" className="sm:col-span-2" placeholder="1234 5678 9012 3456" value={form.cardNumber} onChange={e => update('cardNumber', formatCardNumber(e.target.value))} error={errors.cardNumber} icon={<CreditCardIcon className="h-4 w-4" data-id="element-190" data-mp="kgbqde9pofna" />} data-id="element-189" data-mp="jmzk4c8nvhyy" />
        <FormField id="cardName" label="Nom sur la carte" autoComplete="cc-name" className="sm:col-span-2" value={form.cardName} onChange={e => update('cardName', e.target.value)} error={errors.cardName} data-id="element-191" data-mp="pcsh8g8ap842" />
        <FormField id="cardExpiry" label="Expiration" inputMode="numeric" autoComplete="cc-exp" placeholder="MM/AA" value={form.cardExpiry} onChange={e => update('cardExpiry', formatExpiry(e.target.value))} error={errors.cardExpiry} data-id="element-192" data-mp="13dv3aszaiiez" />
        <FormField id="cardCvc" label="Cryptogramme" inputMode="numeric" autoComplete="cc-csc" placeholder="123" maxLength={3} value={form.cardCvc} onChange={e => update('cardCvc', e.target.value.replace(/\D/g, ''))} error={errors.cardCvc} data-id="element-193" data-mp="1h3ouvj26jgse" />
      </fieldset>
      <p className="mt-3 text-xs text-muted" data-id="element-194" data-mp="1w7rn6jnnya3y">
        Démo : tout numéro à 16 chiffres fonctionne. 4000 0000 0000 0002 simule un refus.
      </p>

      <div className="mt-5" data-id="element-195" data-mp="1r9gb029wbnik">
        <label className="flex cursor-pointer items-start gap-3 text-sm" data-id="element-196" data-mp="1rarfk4b60zaq">
          <input type="checkbox" checked={form.acceptTerms} onChange={e => update('acceptTerms', e.target.checked)} aria-invalid={errors.acceptTerms ? true : undefined} aria-describedby={errors.acceptTerms ? 'terms-error' : undefined} className="mt-0.5 h-4 w-4 accent-clay" data-id="element-197" data-mp="1n8nzt3hgr8jn" />
          <span data-id="element-198" data-mp="266h5gdjh1wkq">
            J’accepte les conditions générales de vente. Les pièces artisanales peuvent présenter de légères variations.
          </span>
        </label>
        {errors.acceptTerms && <p id="terms-error" className="mt-1.5 pl-7 text-xs text-clay" data-id="element-199" data-mp="2xwvbxop5o6md">
            {errors.acceptTerms}
          </p>}
      </div>

      <button type="submit" disabled={processing} className="mt-6 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-clay text-sm font-medium text-paper transition-[background-color,transform] duration-150 ease-out hover:bg-clay-dark active:scale-[0.99] disabled:opacity-80" data-id="element-200" data-mp="2idsn5haox84j">
        <AnimatePresence mode="wait" initial={false} data-id="element-201" data-mp="2dhu3u847ygaz">
          <motion.span key={processing ? 'p' : 'i'} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.15
        }} className="flex items-center gap-2" data-id="element-202" data-mp="316wl3euxy74y">
            {processing ? <>
                <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-203" data-mp="2wcioj94jltyp" /> Paiement en cours…
              </> : <>
                <LockIcon className="h-4 w-4" data-id="element-204" data-mp="3ovfqnmmlw5tc" /> Payer {formatPrice(total)}
              </>}
          </motion.span>
        </AnimatePresence>
      </button>
      <p className="mt-3 text-center text-xs text-muted" data-id="element-205" data-mp="18h41pno425sl">Paiement chiffré et sécurisé · 3D Secure</p>
    </form>;
}